'use client';

import { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { RouterLink } from 'src/routes/components';
import { DashboardContent } from 'src/layouts/dashboard';
import { Iconify } from 'src/components/iconify';
import { EmptyContent } from 'src/components/empty-content';
import { hämtaSenasteFel, type Felpost } from 'src/lib/fel/felbutik';
import { rapporteraKlientfel } from 'src/lib/fel/rapporteraKlientfel';

import { FelrapportDialog } from './FelrapportDialog';

type Props = {
  error: Error & { digest?: string };
  reset?: () => void;
  /** Vart "tillbaka" leder. Utelämnas när vyn inte har någon självklar lista att gå till. */
  tillbakaTill?: string;
  tillbakaText?: string;
  rubrik?: string;
};

/**
 * Felgräns för en enskild vy i dashboarden.
 *
 * De sex ruttgränserna sa tidigare alla "Kunde inte hitta ärende" och länkade till
 * ärendelistan — en kopia som aldrig anpassades. Värre: de påstod att något inte fanns, när
 * det som hänt var att koden kraschat. Nu säger de vad som faktiskt inträffade, och felet
 * hamnar i felloggen i stället för att försvinna.
 */
export function Ruttfel({
  error,
  reset,
  tillbakaTill,
  tillbakaText = 'Tillbaka till listan',
  rubrik = 'Sidan kunde inte visas',
}: Props) {
  const [referens, setReferens] = useState<string | undefined>();
  const [dialogÖppen, setDialogÖppen] = useState(false);
  const [fel, setFel] = useState<Felpost | null>(null);

  useEffect(() => {
    let avbruten = false;
    rapporteraKlientfel(error).then((ref) => {
      if (!avbruten) {
        setReferens(ref);
        setFel(hämtaSenasteFel());
      }
    });
    return () => {
      avbruten = true;
    };
  }, [error]);

  return (
    <DashboardContent sx={{ pt: 5 }}>
      <EmptyContent
        filled
        title={rubrik}
        description="Ett oväntat fel inträffade. Felet har loggats och vi tittar på det."
        action={
          <Stack spacing={1} sx={{ mt: 3, alignItems: 'center' }}>
            {referens && (
              <Alert severity="info" variant="outlined">
                Referens: <strong>{referens}</strong>
              </Alert>
            )}
            <Stack direction="row" spacing={1}>
              {reset && (
                <Button variant="contained" onClick={() => reset()}>
                  Försök igen
                </Button>
              )}
              {referens && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFel(hämtaSenasteFel());
                    setDialogÖppen(true);
                  }}
                >
                  Rapportera
                </Button>
              )}
              {tillbakaTill && (
                <Button
                  component={RouterLink}
                  href={tillbakaTill}
                  color="inherit"
                  startIcon={<Iconify width={16} icon="eva:arrow-ios-back-fill" />}
                >
                  {tillbakaText}
                </Button>
              )}
            </Stack>
          </Stack>
        }
        sx={{ py: 10, height: 'auto', flexGrow: 'unset' }}
      />

      <FelrapportDialog öppen={dialogÖppen} fel={fel} vidStängning={() => setDialogÖppen(false)} />
    </DashboardContent>
  );
}
